import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const csPrinciplesUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Data",
  examWeight: "17–22%",
  focus: "Data are central to computing innovations. Computers consume, transform, and produce data to allow users to create new knowledge.",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Binary Numbers",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💻 2.1.1 The Architecture of Data (Bits and Bytes)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              At the physical layer, computers are made of hardware components (transistors, capacitors) that exist in one of two states: <strong>On</strong> (high voltage) or <strong>Off</strong> (low voltage).
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Bit (Binary Digit)" 
                definition="The smallest unit of data in a computer. It represents a single value of either 0 or 1."
              />
              <KeyTerm 
                term="Byte" 
                definition="A sequence of 8 bits. (e.g., 10110001)."
              />
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">🎛️ Abstraction</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Computers use layers of abstraction. We see images, videos, and text, but deep down, the computer only "sees" bits.
                </p>
                <p className="text-xs text-slate-600 mt-2 italic">
                  Note: The same sequence of bits can represent different types of data (a number, a character, a color) depending on the context in which the program interprets it.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔢 2.1.2 Number Systems: Decimal vs. Binary</h3>
            <p className="text-slate-700 mb-4">To understand binary, you must understand the <strong>positional number system</strong>.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-slate-900 border-b mb-2">Decimal (Base 10)</h4>
                <ul className="text-sm text-slate-700 space-y-1">
                  <li>• Uses 10 digits: 0–9.</li>
                  <li>• Place values: <InlineMath math="10^2" /> (100s), <InlineMath math="10^1" /> (10s), <InlineMath math="10^0" /> (1s).</li>
                  <li className="pt-2 font-mono text-xs text-blue-600">Example: 231 = (2 × 100) + (3 × 10) + (1 × 1)</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                <h4 className="font-bold text-blue-900 border-b mb-2">Binary (Base 2)</h4>
                <ul className="text-sm text-slate-700 space-y-1">
                  <li>• Uses 2 digits: 0 and 1.</li>
                  <li>• Place values: <InlineMath math="2^3" /> (8s), <InlineMath math="2^2" /> (4s), <InlineMath math="2^1" /> (2s), <InlineMath math="2^0" /> (1s).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 2.1.3 Converting Binary to Decimal</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700 mb-3">Add up the place values where there is a <code>1</code>.</p>
              <div className="bg-white p-4 rounded border border-blue-100 font-mono text-sm">
                <p className="font-bold mb-2 underline">Example: Convert 1011 (binary) to decimal</p>
                <ul className="space-y-1">
                  <li>8s place: 1 (Include)</li>
                  <li>4s place: 0 (Exclude)</li>
                  <li>2s place: 1 (Include)</li>
                  <li>1s place: 1 (Include)</li>
                  <li className="pt-2 border-t mt-2 text-blue-700 font-bold italic">Calculation: 8 + 0 + 2 + 1 = 11</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 2.1.4 Converting Decimal to Binary</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700 mb-3">Find the largest power of 2 that fits, subtract it, and repeat with the remainder.</p>
              <div className="bg-white p-4 rounded border border-green-100 font-mono text-sm">
                <p className="font-bold mb-2 underline">Example: Convert 13 (decimal) to binary</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Largest power of 2 {"<"} 13 is <strong>8</strong>. Put a 1 in 8s place. (Rem: 5)</li>
                  <li>Largest power of 2 {"<"} 5 is <strong>4</strong>. Put a 1 in 4s place. (Rem: 1)</li>
                  <li>Does 2 fit into 1? No. Put a <strong>0</strong> in 2s place.</li>
                  <li>Does 1 fit into 1? Yes. Put a <strong>1</strong> in 1s place.</li>
                  <li className="pt-2 border-t mt-2 text-green-700 font-bold">Result: 1101</li>
                </ol>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌊 2.1.5 Analog vs. Digital Data</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="p-4 rounded-lg border border-orange-200 bg-orange-50">
                <h4 className="font-bold text-orange-900">Analog Data</h4>
                <p className="text-sm text-slate-700">Values that change continuously, or smoothly, over time (e.g., sound waves, oil paintings, temperature).</p>
              </div>
              <div className="p-4 rounded-lg border border-teal-200 bg-teal-50">
                <h4 className="font-bold text-teal-900">Digital Data</h4>
                <p className="text-sm text-slate-700">Data that changes in discrete intervals (steps).</p>
              </div>
            </div>
            <div className="bg-slate-100 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-800">🎙️ Sampling</h4>
              <p className="text-sm text-slate-700 mt-1">The technique used to convert analog data into digital data.</p>
              <ol className="list-decimal pl-6 mt-2 text-sm text-slate-700 space-y-1">
                <li>Computer measures the analog wave at regular intervals (samples).</li>
                <li>It assigns a binary value to each measurement.</li>
              </ol>
              <p className="text-xs font-bold text-slate-600 mt-3 italic">Key Concept: To improve quality, increase the sample rate (measure more often) or bit depth (more bits per measurement).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ 2.1.6 Limitations of Bits</h3>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Overflow Error</h4>
                <p className="text-sm text-slate-700 mt-1">Occurs when a calculation produces a result larger than the computer can store with the allocated bits.</p>
                <p className="text-xs text-red-700 mt-2"><em>Example:</em> In a 4-bit system (max 15), calculating 15 + 1 may crash or display 0 (like a car odometer rolling from 999,999 to 000,000).</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Round-off Error</h4>
                <p className="text-sm text-slate-700 mt-1">Occurs with floating-point (decimal) numbers. Because some fractions (like 1/3 or 0.1) have infinite repeating binary sequences, the computer must round them, leading to slight imprecision.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Data Compression",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed">Data compression reduces the number of bits required to represent data, saving <strong>storage space</strong> and <strong>transmission time</strong>.</p>
          
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗜️ 2.2.1 Redundancy</h3>
            <p className="text-sm text-slate-700">Compression relies on repeating patterns. High redundancy allows for significant compression.</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📋 2.2.2 Lossless Compression</h3>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <p className="text-sm text-slate-700">Reduces bits while guaranteeing <strong>complete reconstruction</strong> of original data.</p>
              <ul className="list-disc pl-5 mt-2 text-xs text-slate-600 space-y-1">
                <li><strong>How:</strong> Looks for patterns and creates a dictionary/key.</li>
                <li><strong>Example:</strong> Run-Length Encoding ("BBBBBBBB" → "8B").</li>
                <li><strong>Use Case:</strong> Text files, code, bank records (where losing a single bit is critical).</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🖼️ 2.2.3 Lossy Compression</h3>
            <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
              <p className="text-sm text-slate-700">Significantly reduces bits but only reconstructs an <strong>approximation</strong>.</p>
              <ul className="list-disc pl-5 mt-2 text-xs text-slate-600 space-y-1">
                <li><strong>How:</strong> Discards data human senses are unlikely to notice (subtle color/sound frequencies).</li>
                <li><strong>Use Case:</strong> Images (JPEG), Audio (MP3), Video.</li>
                <li><strong>Warning:</strong> Original data is thrown away and <strong>cannot</strong> be restored.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 2.2.4 Comparison Summary</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-slate-200 rounded-lg">
                <thead className="bg-slate-100 font-bold text-slate-700">
                  <tr>
                    <th className="px-4 py-2 border-b text-left">Feature</th>
                    <th className="px-4 py-2 border-b text-left">Lossless</th>
                    <th className="px-4 py-2 border-b text-left">Lossy</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-slate-600 divide-y divide-slate-200">
                  <tr>
                    <td className="px-4 py-2 font-bold bg-slate-50">Size Reduction</td>
                    <td className="px-4 py-2">Moderate</td>
                    <td className="px-4 py-2">Significant (High)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-bold bg-slate-50">Quality</td>
                    <td className="px-4 py-2">Perfect (Identical)</td>
                    <td className="px-4 py-2">Lower (Approximation)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-bold bg-slate-50">Reversibility</td>
                    <td className="px-4 py-2">Reversible</td>
                    <td className="px-4 py-2">Irreversible</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-bold bg-slate-50">Use Case</td>
                    <td className="px-4 py-2">Text, Code, Archives</td>
                    <td className="px-4 py-2">Images, Audio, Video</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Extracting Information from Data",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧠 2.3.1 Data vs. Information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 border rounded-lg">
                <span className="font-bold text-slate-800">Data:</span> Raw facts/figures (e.g., 70, 72, 68).
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="font-bold text-green-800">Information:</span> Knowledge extracted (e.g., "Trending downward").
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏷️ 2.3.2 Metadata</h3>
            <p className="text-slate-700 mb-3"><strong>Metadata</strong> is "data about data." It describes the primary data but is distinct from it.</p>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 space-y-2">
              <p className="text-sm">• <strong>Image file:</strong> Resolution, timestamp, geolocation, camera model.</p>
              <p className="text-sm">• <strong>Web packet:</strong> "To" and "From" IP addresses.</p>
              <p className="text-xs font-bold text-amber-900 mt-2">Utility: Metadata helps organize and search. Changing metadata does NOT change the actual data content.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 2.3.3 Correlation vs. Causation</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700"><strong>Correlation:</strong> A relationship where two things change together (e.g., ice cream sales and shark attacks).</p>
              <p className="text-sm text-slate-700"><strong>Causation:</strong> One thing actually causes the other.</p>
              <ImportantNote>Computers find correlation. Humans must determine causation.</ImportantNote>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚧 2.3.4 Challenges in Data Processing</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-white border rounded-lg shadow-sm">
                <h4 className="font-bold text-slate-800 text-sm">🧹 Cleaning Data</h4>
                <p className="text-xs text-slate-600 mt-1">Fixing messy/inconsistent entries (e.g., "NYC" vs "New York City") so the computer recognizes them as the same.</p>
              </div>
              <div className="p-4 bg-white border rounded-lg shadow-sm">
                <h4 className="font-bold text-slate-800 text-sm">⚖️ Bias</h4>
                <p className="text-xs text-slate-600 mt-1">Data from a limited group creates skewed results. More data doesn't fix a flawed collection method.</p>
              </div>
              <div className="p-4 bg-white border rounded-lg shadow-sm">
                <h4 className="font-bold text-slate-800 text-sm">🔒 Privacy</h4>
                <p className="text-xs text-slate-600 mt-1">PII (Personally Identifiable Information) requires secure handling and anonymization.</p>
              </div>
              <div className="p-4 bg-white border rounded-lg shadow-sm">
                <h4 className="font-bold text-slate-800 text-sm">❓ Incomplete Data</h4>
                <p className="text-xs text-slate-600 mt-1">Missing values can lead to incorrect conclusions.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 2.3.5 Scalability and Big Data</h3>
            <div className="space-y-3">
              <KeyTerm term="Scalability" definition="The ability of a system to adapt to dealing with larger amounts of data." />
              <KeyTerm term="Parallel Processing" definition="Splitting datasets across multiple computers working simultaneously to process data in a reasonable time." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Using Programs with Data",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ 2.4.1 Tools for Analysis</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-center">
                <h5 className="font-bold text-sm">Spreadsheets</h5>
                <p className="text-xs text-slate-600 mt-1">Small/Medium sets; sorting, filtering, formulas.</p>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-center">
                <h5 className="font-bold text-sm">Databases</h5>
                <p className="text-xs text-slate-600 mt-1">Large, searchable structured datasets.</p>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-center">
                <h5 className="font-bold text-sm">Programming</h5>
                <p className="text-xs text-slate-600 mt-1">Python/SQL for Big Data, complex automation, and cleaning.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ 2.4.2 Processing Techniques</h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex gap-2"><span className="font-bold text-blue-600">1. Transforming:</span> Modifying every element (e.g., converting Dollars to Euros).</li>
              <li className="flex gap-2"><span className="font-bold text-blue-600">2. Filtering:</span> Creating a subset based on a condition (e.g., GPA {">"} 3.5).</li>
              <li className="flex gap-2"><span className="font-bold text-blue-600">3. Combining:</span> Merging datasets (e.g., linking customer names with recent purchases).</li>
              <li className="flex gap-2"><span className="font-bold text-blue-600">4. Clustering/Classifying:</span> Grouping data based on similarities (Machine Learning).</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎨 2.4.3 Visualizing Data</h3>
            <p className="text-sm text-slate-700 mb-3">Charts, graphs, and heat maps are <strong>computational artifacts</strong> used to help humans understand massive datasets.</p>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 text-sm">
              <p>• <strong>Trend Spotting:</strong> Visuals make patterns and outliers immediately apparent.</p>
              <p>• <strong>Communication:</strong> Tables are for looking up values; charts are for seeing the "big picture."</p>
            </div>
          </section>

          <section className="mt-8 pt-6 border-t border-slate-300">
            <div className="bg-slate-800 rounded-xl p-6 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-4">✅ Unit 2 Summary Checklist</h3>
              <ul className="grid md:grid-cols-2 gap-4 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 font-bold">✓</span> Can you convert between Binary and Decimal?
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 font-bold">✓</span> Explain difference between Lossy and Lossless?
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 font-bold">✓</span> Do you understand bit/byte overflow errors?
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 font-bold">✓</span> Can you define metadata and give an example?
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 font-bold">✓</span> Do you understand correlation != causation?
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 font-bold">✓</span> Identify when data needs to be "cleaned"?
                </li>
              </ul>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default csPrinciplesUnit2Notes;